import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

// Content Exclusion Filters — a single, in-memory choice shared across the
// Screening Room rails, both full-list pages, and in-app search. Toggling an
// exclusion anywhere updates this one source of truth so every surface
// reflects it instantly. SESSION-ONLY by default (a page reload or a new
// visit starts back at "show everything") — unless the person opts in via
// "Remember these choices", in which case the current genre/rating picks are
// also saved on their account (GET/PUT /api/discover/exclusion-prefs) and
// loaded back in automatically on their next visit — including, via the
// cross-device refresh below, on an already-open tab/device without needing
// a manual reload.
const ContentExclusionContext = createContext(null);

export function ContentExclusionProvider({ children }) {
  const { user } = useAuth();
  const uid = user ? user.user_id : null;
  const [excludeGenres, setExcludeGenres] = useState([]);
  const [excludeRatings, setExcludeRatings] = useState([]);
  const [remembered, setRememberedState] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);
  const [suggestion, setSuggestion] = useState([]); // "Smarter Excludes" — genre keys Bari suggests, from Taste Profile dislikes
  const [unexcludeSuggestion, setUnexcludeSuggestion] = useState([]); // "Suggestion History" — genre keys Bari suggests UN-excluding, since dismissed
  const [rememberNudge, setRememberNudge] = useState(false); // "Smart Defaults Nudge" — gentle one-time prompt to turn Remember on
  const [prefsLoadedFor, setPrefsLoadedFor] = useState(null); // the uid we last loaded prefs for
  const skipNextSyncRef = useRef(false);
  const wasRememberedRef = useRef(false);
  const lastLocalEditRef = useRef(0); // timestamp of the last local toggle, so a cross-device refetch never clobbers an in-flight edit
  const sessionPingSentRef = useRef(false); // guards the once-per-visit "session had active, non-remembered exclusions" ping
  const activeCount = excludeGenres.length + excludeRatings.length;

  const loadPrefs = useCallback(async (forUid) => {
    try {
      const r = await client.get("/discover/exclusion-prefs");
      const { remembered: rem, genres, ratings } = r.data;
      if (rem) {
        skipNextSyncRef.current = true; // this echoes what we just loaded — nothing new to save
        wasRememberedRef.current = true;
        setRememberedState(true);
        setExcludeGenres(genres || []);
        setExcludeRatings(ratings || []);
      } else if (wasRememberedRef.current) {
        // "Remember" was turned off on another device — mirror that here too.
        skipNextSyncRef.current = true;
        wasRememberedRef.current = false;
        setRememberedState(false);
        setExcludeGenres([]); setExcludeRatings([]);
      }
    } catch { /* best-effort */ }
    setPrefsLoadedFor(forUid);
  }, []);

  const loadSuggestion = useCallback(async () => {
    try {
      const r = await client.get("/discover/exclude-suggestion");
      setSuggestion(r.data?.genres || []);
    } catch { /* best-effort */ }
  }, []);

  const loadUnexcludeSuggestion = useCallback(async () => {
    try {
      const r = await client.get("/discover/exclude-unsuggestion");
      setUnexcludeSuggestion(r.data?.genres || []);
    } catch { /* best-effort */ }
  }, []);

  const loadRememberNudge = useCallback(async () => {
    try {
      const r = await client.get("/discover/remember-nudge");
      setRememberNudge(!!r.data?.show);
    } catch { /* best-effort */ }
  }, []);

  // Load (or reset) the saved default whenever WHO is signed in changes —
  // including a straight account switch within the same tab (e.g. sign out
  // of A, sign into B without a hard reload): each uid gets its own load,
  // and switching away from a remembered account never carries its
  // exclusions over into the next one.
  useEffect(() => {
    if (!uid) {
      setExcludeGenres([]); setExcludeRatings([]); setRememberedState(false);
      wasRememberedRef.current = false; setPrefsLoadedFor(null); setCustomPresets([]); setSuggestion([]);
      setUnexcludeSuggestion([]); setRememberNudge(false); sessionPingSentRef.current = false;
      return;
    }
    let cancelled = false;
    setExcludeGenres([]); setExcludeRatings([]); setRememberedState(false); setCustomPresets([]); setSuggestion([]);
    setUnexcludeSuggestion([]); setRememberNudge(false); sessionPingSentRef.current = false;
    wasRememberedRef.current = false;
    loadPrefs(uid);
    client.get("/discover/custom-presets").then((r) => {
      if (!cancelled) setCustomPresets(r.data?.presets || []);
    }).catch(() => {});
    loadSuggestion();
    loadUnexcludeSuggestion();
    loadRememberNudge();
    return () => { cancelled = true; };
  }, [uid, loadPrefs, loadSuggestion, loadUnexcludeSuggestion, loadRememberNudge]);

  // Cross-device sync — a "Remembered" default changed on another device/tab,
  // or a newly-disliked genre added in Settings > Profile (same account, a
  // different tab/page), shows up here on the next tab-focus (the moment it
  // actually matters, with zero risk of racing an in-flight edit on THIS
  // device since nothing local could have changed while this tab was in the
  // background) plus a 5-minute floor so a long-lived background tab doesn't
  // go too stale.
  useAutoRefresh(useCallback(() => {
    if (!uid || Date.now() - lastLocalEditRef.current < 4000) return;
    loadPrefs(uid);
    loadSuggestion();
    loadUnexcludeSuggestion();
  }, [uid, loadPrefs, loadSuggestion, loadUnexcludeSuggestion]), 5 * 60 * 1000);

  // "Smart Defaults Nudge" — the first time THIS session has an active,
  // non-remembered exclusion selection, tell the backend (it tallies
  // sessions and, after a few, flags the nudge below). Fires once per
  // mount, well after the initial load so it never counts the very first
  // render of a freshly-restored remembered selection.
  useEffect(() => {
    if (!uid || sessionPingSentRef.current) return;
    if (remembered || activeCount === 0) return;
    sessionPingSentRef.current = true;
    client.post("/discover/exclusion-session-ping").then(loadRememberNudge).catch(() => {});
  }, [uid, remembered, activeCount, loadRememberNudge]);

  const toggleGenre = useCallback((key) => {
    lastLocalEditRef.current = Date.now();
    setExcludeGenres((prev) => {
      const adding = !prev.includes(key);
      // Manually re-excluding a genre Bari flagged as "no longer disliked,
      // stop excluding?" means the person wants it excluded after all —
      // clear that stale watermark so the un-exclude card doesn't
      // immediately contradict what they just did.
      if (adding) {
        setUnexcludeSuggestion((u) => {
          if (!u.includes(key)) return u;
          client.post("/discover/exclude-unsuggestion/dismiss", { genres: [key] }).catch(() => {});
          return u.filter((g) => g !== key);
        });
      }
      return adding ? [...prev, key] : prev.filter((k) => k !== key);
    });
  }, []);
  const toggleRating = useCallback((key) => {
    lastLocalEditRef.current = Date.now();
    setExcludeRatings((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);
  const clearAll = useCallback(() => {
    lastLocalEditRef.current = Date.now();
    setExcludeGenres([]); setExcludeRatings([]);
  }, []);
  // "Kids-Safe" and any future one-tap preset: replace the current selection
  // outright rather than merging, so the control reads as a clean on/off
  // switch, not an accumulating pile of toggles.
  const applyExact = useCallback((genres, ratings) => {
    lastLocalEditRef.current = Date.now();
    setExcludeGenres(genres); setExcludeRatings(ratings);
  }, []);
  const setRemembered = useCallback((v) => { lastLocalEditRef.current = Date.now(); setRememberedState(v); }, []);

  // "Smarter Excludes" — accept or decline Bari's suggested preset (built
  // from the Taste Profile's "genres you don't enjoy"). Accepting adds those
  // genres to the current exclusions (additive — never wipes an existing
  // manual pick) and dismisses too, so it doesn't resurface if later
  // manually removed.
  const applySuggestion = useCallback(() => {
    if (!suggestion.length) return;
    lastLocalEditRef.current = Date.now();
    setExcludeGenres((prev) => Array.from(new Set([...prev, ...suggestion])));
    client.post("/discover/exclude-suggestion/dismiss", { genres: suggestion, applied: true }).catch(() => {});
    setSuggestion([]);
  }, [suggestion]);
  const dismissSuggestion = useCallback(() => {
    if (!suggestion.length) return;
    client.post("/discover/exclude-suggestion/dismiss", { genres: suggestion }).catch(() => {});
    setSuggestion([]);
  }, [suggestion]);

  // "Suggestion History" — accept or decline Bari's offer to un-exclude a
  // genre that's no longer disliked. Only acts on the subset that's
  // actually still excluded right now (what the card shows) — a genre that
  // was already turned off locally stays silently tracked rather than
  // being dropped underneath an unrelated click.
  const acceptUnexclude = useCallback(() => {
    const visible = unexcludeSuggestion.filter((g) => excludeGenres.includes(g));
    if (!visible.length) return;
    lastLocalEditRef.current = Date.now();
    setExcludeGenres((prev) => prev.filter((g) => !visible.includes(g)));
    client.post("/discover/exclude-unsuggestion/dismiss", { genres: visible }).catch(() => {});
    setUnexcludeSuggestion((prev) => prev.filter((g) => !visible.includes(g)));
  }, [unexcludeSuggestion, excludeGenres]);
  const dismissUnexclude = useCallback(() => {
    const visible = unexcludeSuggestion.filter((g) => excludeGenres.includes(g));
    if (!visible.length) return;
    client.post("/discover/exclude-unsuggestion/dismiss", { genres: visible }).catch(() => {});
    setUnexcludeSuggestion((prev) => prev.filter((g) => !visible.includes(g)));
  }, [unexcludeSuggestion, excludeGenres]);

  // "Smart Defaults Nudge" — accept turns Remember on (which also persists
  // the current picks via the sync effect below); either response ends the
  // nudge for good.
  const acceptRememberNudge = useCallback(() => {
    lastLocalEditRef.current = Date.now();
    setRememberedState(true);
    client.post("/discover/remember-nudge/dismiss").catch(() => {});
    setRememberNudge(false);
  }, []);
  const dismissRememberNudge = useCallback(() => {
    client.post("/discover/remember-nudge/dismiss").catch(() => {});
    setRememberNudge(false);
  }, []);

  // "Save my current picks as a named preset" — persists on the account
  // (independent of the session-only "Remember" toggle above) so it's
  // available as a one-tap option on any device, any time.
  const saveCustomPreset = useCallback(async (name) => {
    const { data } = await client.post("/discover/custom-presets", {
      name, genres: excludeGenres, ratings: excludeRatings,
    });
    setCustomPresets(data.presets || []);
  }, [excludeGenres, excludeRatings]);
  const deleteCustomPreset = useCallback(async (name) => {
    const { data } = await client.delete(`/discover/custom-presets/${encodeURIComponent(name)}`);
    setCustomPresets(data.presets || []);
  }, []);

  // While "Remember" is on, keep the saved copy in sync with every change.
  // Turning it off clears the saved copy too (one PUT), but leaves THIS
  // visit's current selection alone — only the NEXT visit reverts to
  // default. Skipped for the common case (never opted in, nothing to sync
  // or clear) and for the one sync right after boot-loading a remembered
  // copy (that PUT would just echo back what the GET already returned).
  useEffect(() => {
    if (!uid || prefsLoadedFor !== uid) return;
    if (skipNextSyncRef.current) { skipNextSyncRef.current = false; return; }
    const was = wasRememberedRef.current;
    wasRememberedRef.current = remembered;
    if (!remembered && !was) return;
    client.put("/discover/exclusion-prefs", {
      remembered, genres: remembered ? excludeGenres : [], ratings: remembered ? excludeRatings : [],
    }).catch(() => {});
  }, [uid, prefsLoadedFor, remembered, excludeGenres, excludeRatings]);

  // Ready-to-spread query params for every fetch call that needs to respect
  // the current exclusions (rails, full-list pages, search).
  const exclusionParams = useMemo(() => {
    const p = {};
    if (excludeGenres.length) p.exclude_genres = excludeGenres.join(",");
    if (excludeRatings.length) p.exclude_ratings = excludeRatings.join(",");
    return p;
  }, [excludeGenres, excludeRatings]);

  // Exposed so Settings > Profile can call this immediately after saving
  // disliked genres, instead of waiting for the next window-focus refresh.
  const refreshSuggestions = useCallback(() => {
    loadSuggestion();
    loadUnexcludeSuggestion();
  }, [loadSuggestion, loadUnexcludeSuggestion]);

  const value = useMemo(() => ({
    excludeGenres, excludeRatings, toggleGenre, toggleRating, clearAll, applyExact,
    remembered, setRemembered, activeCount, exclusionParams,
    customPresets, saveCustomPreset, deleteCustomPreset,
    suggestion, applySuggestion, dismissSuggestion,
    unexcludeSuggestion, acceptUnexclude, dismissUnexclude, refreshSuggestions,
    rememberNudge, acceptRememberNudge, dismissRememberNudge,
  }), [excludeGenres, excludeRatings, toggleGenre, toggleRating, clearAll, applyExact,
      remembered, setRemembered, activeCount, exclusionParams,
      customPresets, saveCustomPreset, deleteCustomPreset,
      suggestion, applySuggestion, dismissSuggestion,
      unexcludeSuggestion, acceptUnexclude, dismissUnexclude, refreshSuggestions,
      rememberNudge, acceptRememberNudge, dismissRememberNudge]);

  return <ContentExclusionContext.Provider value={value}>{children}</ContentExclusionContext.Provider>;
}

export function useContentExclusions() {
  const ctx = useContext(ContentExclusionContext);
  if (!ctx) throw new Error("useContentExclusions must be used within a ContentExclusionProvider");
  return ctx;
}
